import { UseFormGetValues } from 'react-hook-form';
import { notificationCriteriaValidator } from 'pages/workbench/workbench_tabs/shared/notification_criteria/shared';
import { AlarmThresholdFormFields, MetricTypeConfig } from './shared';

export const warningFieldRules = {
    valueAsNumber: true,
    validate: (value: number | undefined) => validateWarningPercentage(value),
};

export const criticalAlarmRules = {
    valueAsNumber: true,
    validate: (value: number | undefined) => validateCriticalAlarmPercentage(value),
};

export function validateWarningPercentage(value: number | undefined) {
    if (value === undefined || Number.isNaN(value)) {
        return 'Warning percentage is required';
    }

    if (value < 1) {
        return 'Warning percentage must be at least 1%';
    }

    if (value > 99) {
        return 'Warning percentage must be less than 100%';
    }

    return true;
}

export function validateCriticalAlarmPercentage(value: number | undefined) {
    if (value === undefined || Number.isNaN(value)) {
        return 'Critical alarm percentage is required';
    }

    if (value < 101) {
        return 'Critical alarm percentage must be at least 101%';
    }

    if (value > 500) {
        return 'Critical alarm percentage must be less than 500%';
    }

    return true;
}

export function validateAtLeastOneSelectedIndicator(value: MetricTypeConfig[]) {
    return (
        value.some(({ selected }) => selected) || 'Please select at least one indicator.'
    );
}

export function validateGroupIds(value: string[]) {
    return value.length > 0 || 'At least one group must be selected';
}

export function minAlarmBoundRules(
    index: number,
    getValues: UseFormGetValues<AlarmThresholdFormFields>,
) {
    return {
        valueAsNumber: true,
        deps: [
            `indicatorTypes.${index}.maxAlarmBound`,
            `indicatorTypes.${index}.selected`,
        ],
        validate: (minValue: number | undefined) => {
            const indicator = getValues(`indicatorTypes.${index}`);

            if (!indicator.selected) {
                return true;
            }

            if (minValue === undefined || Number.isNaN(minValue)) {
                return 'Min alarm bound is required';
            }

            if (minValue < 0) {
                return 'Min alarm bound must be greater than or equal to 0';
            }

            if (
                indicator.maxAlarmBound !== undefined &&
                minValue > indicator.maxAlarmBound
            ) {
                return 'Min alarm bound must be less than or equal to max alarm bound';
            }

            return true;
        },
    };
}

export function maxAlarmBoundRules(
    index: number,
    getValues: UseFormGetValues<AlarmThresholdFormFields>,
) {
    return {
        valueAsNumber: true,
        deps: [
            `indicatorTypes.${index}.selected`,
            `indicatorTypes.${index}.minAlarmBound`,
        ],
        validate: (maxValue: number | undefined) => {
            const indicator = getValues(`indicatorTypes.${index}`);

            if (!indicator.selected) {
                return true;
            }

            if (maxValue === undefined || Number.isNaN(maxValue)) {
                return 'Max alarm bound is required';
            }

            if (maxValue < 0) {
                return 'Max alarm bound must be greater than or equal to 0';
            }

            if (
                indicator.minAlarmBound !== undefined &&
                maxValue < indicator.minAlarmBound
            ) {
                return 'Max alarm bound must be greater than or equal to min alarm bound';
            }

            return true;
        },
    };
}

function asCriteriaString(value: string | number | undefined | null) {
    if (value === undefined || value === null) {
        return undefined;
    }

    return value.toString();
}

export function validateNotificationCriteria(
    value: string | number | undefined | null,
    type: 'percent' | 'points',
) {
    const valueString = asCriteriaString(value);

    // TODO: @ari when the alarm thresholds tool is updated to work with data platform,
    // this function will need updated to use the form values instead of the hardcoded 'minutes' unit
    return notificationCriteriaValidator(valueString, undefined, type, 'minutes');
}

export const percentageFieldRules = {
    required: 'Please enter notification criteria percent',
};

export const pointsFieldRules = {
    required: 'Please enter notification criteria points',
};
